import React from "react";

import { parse, format } from "date-fns";
import { useGetCurrentCompanyApplicationQuery, useResolvePendingItemMutation } from "EmployerApplication/api";
import createDecorator from "final-form-focus";
import { quotePageURL } from "QuotePage/urls";
import { Form } from "react-final-form";
import { deserializeDate } from "Redux/serializers";
import AnalyticsTracking from "Utils/AnalyticsTracking";

import { SIFinalFormField, SIFormRadioGroup } from "Common/forms";
import { SaveButton } from "Common/forms/buttons";
import { Row, Col } from "Common/Grid";
import { Div } from "Common/HTML";
import { appendAuthParams } from "Common/TokenAuth/Utils";
import { Body2 } from "Theme/BodyText";
import Button from "Theme/Button";

import makeBem from "Utils/makeBem";

const bem = makeBem("ApplicationStepCard");
const focusOnErrors = createDecorator();

const goToQuotePage = () => {
  AnalyticsTracking.trackEvent("cta_clicked", {
    location: "missed_deadline_page",
    action: "change_plan",
  });

  window.location.replace(appendAuthParams(quotePageURL()));
};

const MissedDeadline = () => {
  // Don't need to check loading for now, as only the App status Page uses this component,
  // and that is is gated by the loading status of the company application.
  const { data: companyApplication, isLoading } = useGetCurrentCompanyApplicationQuery();
  const companyPolicyId = companyApplication?.id;

  const [resolvePendingItem] = useResolvePendingItemMutation();
  // See comment above on why this will never be seen.  It should be replaced with a loading
  // component that actually reflects what it is, but the structure of this component needs
  // to be rethought as well.
  if (isLoading) {
    return <>"Loading"</>;
  }

  const coverage = parse(companyApplication.coverageStartDateAt, "MM/dd/yyyy", new Date());

  const carrierDeadline = parse(companyApplication.carrierDeadlines[companyApplication.coverageStartDateAt], "yyyy-MM-dd", new Date());

  let alternativeCarriers = [];
  if (companyApplication.alternativeCarriersForCoverageStartDate) {
    alternativeCarriers = companyApplication.alternativeCarriersForCoverageStartDate[companyApplication.coverageStartDateAt];
  }

  const nextCarrierDeadline = deserializeDate(companyApplication.nextCarrierDeadline);

  // --------------------------

  const missedDeadlineLabel = (
    <Div className={bem("PendingItemsLabel")}>
      {companyApplication.planBrand}'s deadline for {format(coverage, "LLLL do")} coverage was {format(carrierDeadline, "LLLL do")}. Please choose how you would
      like to proceed:
    </Div>
  );

  const missedDeadlineOptions = {
    updateCoverageStartDate: `Change coverage start date to ${format(nextCarrierDeadline, "LLLL do")}`,
    changeCarrier: `Choose a carrier with a later deadline for ${format(coverage, "LLLL do")} coverage`,
    acknowledge: `Apply for ${format(coverage, "LLLL do")} coverage anyway; I understand coverage start may be pushed to ${format(
      nextCarrierDeadline,
      "LLLL do",
    )}`,
  };

  if (alternativeCarriers.length == 0) {
    delete missedDeadlineOptions.changeCarrier;
  }

  const changeCarrierText = (
    <Div>
      <Body2>
        Carrier deadlines for {format(coverage, "LLLL do")} coverage start:
        <p />
        <ul style={{ paddingLeft: "17px" }}>
          {alternativeCarriers.map((carrier) => (
            <li key={carrier.brand_name}>
              {carrier.brand_name} - {format(new Date(carrier.deadline), "LLLL do")}
            </li>
          ))}
        </ul>
        <p />
      </Body2>
      <Button color="gray" size="small" style={{ minWidth: "258px" }} onClick={goToQuotePage}>
        Choose a Different Plan
      </Button>
    </Div>
  );

  const submitHandler = async (values) => {
    let trackingAction;
    const selectedCoverageStartDateResolution = values.missedDeadline;

    if (selectedCoverageStartDateResolution == "updateCoverageStartDate") {
      trackingAction = "change_coverage_start_date";

      await resolvePendingItem({
        companyPolicyId,
        type: "coverage_start_date",
        resolution_type: "update_coverage_start_date",
        coverage_start_date_at: nextCarrierDeadline,
      });
    } else if (selectedCoverageStartDateResolution == "acknowledge") {
      trackingAction = "acknowledge";

      await resolvePendingItem({
        companyPolicyId,
        type: "coverage_start_date",
        resolution_type: "acknowledge",
      });
    }

    AnalyticsTracking.trackEvent("cta_clicked", {
      location: "missed_deadline_page",
      action: trackingAction,
    });
  };

  return (
    <Form onSubmit={submitHandler} decorators={[focusOnErrors]} keepDirtyOnReinitialize>
      {({ handleSubmit, submitting, values }) => (
        <form onSubmit={handleSubmit}>
          <Row>
            <SIFinalFormField
              name="missedDeadline"
              label={missedDeadlineLabel}
              component={SIFormRadioGroup}
              componentProps={{
                options: missedDeadlineOptions,
              }}
              className={bem(null, "pendingItemRadios")}
            />
          </Row>
          {values.missedDeadline == "changeCarrier" && changeCarrierText}
          <Row>
            <Col>
              <SaveButton
                submitting={submitting}
                buttonText="Submit"
                disabled={values.missedDeadline != "updateCoverageStartDate" && values.missedDeadline != "acknowledge"}
                size="small"
                mt={3}
              />
            </Col>
          </Row>
        </form>
      )}
    </Form>
  );
};

export default MissedDeadline;
